import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { getWeeklyQuiz, WeeklyQuizResponse, getMyQuizCategories, type MyQuizCategory, getMyLives, type LivesState } from "../../services/quizzes";
import { resolveMediaUrl } from "../student/quiz/media";
import MobileAppLayout from "../../layouts/MobileAppLayout";
import { GameIcon, GameButton, GameCard, GameHero, LivesBadge, Skeleton } from "../../components/game";
import { formatDate } from "../../lib/dateFormat";

// Deterministic cover gradients for categories without an image, so each
// category gets a stable, distinct colour instead of a washed-out placeholder.
const CATEGORY_GRADIENTS = [
  "from-indigo-500 to-blue-500",
  "from-violet-500 to-fuchsia-500",
  "from-emerald-500 to-teal-500",
  "from-amber-500 to-orange-500",
  "from-rose-500 to-pink-500",
  "from-sky-500 to-cyan-500",
  "from-purple-500 to-indigo-500",
  "from-lime-500 to-emerald-500",
];

function hashString(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h;
}

export default function AppQuizzes() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [categories, setCategories] = useState<MyQuizCategory[]>([]);
  const [data, setData] = useState<WeeklyQuizResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [countdown, setCountdown] = useState("");
  // Lives (hearts) — surfaced here so athletes can see them without opening a
  // quiz. The regen countdown ticks from the fetch snapshot.
  const [livesInfo, setLivesInfo] = useState<LivesState | null>(null);
  const livesFetchedAt = useRef<number>(0);
  const [nowTs, setNowTs] = useState<number>(() => Date.now());

  const loadData = async () => {
    setLoading(true);
    setError(false);
    // Both fetches swallow their own rejections (so one can fail without
    // blanking the other), so track a category-load failure explicitly —
    // categories are the primary content and an empty grid otherwise looks
    // identical to "no quizzes yet".
    let categoriesFailed = false;
    try {
      const [cats, weekly] = await Promise.all([
        getMyQuizCategories().catch((e) => {
          console.error("Failed to load quiz categories:", e);
          categoriesFailed = true;
          return [] as MyQuizCategory[];
        }),
        getWeeklyQuiz().catch((e) => {
          console.error("Failed to load weekly quiz:", e);
          return null;
        }),
      ]);
      setCategories(cats);
      setData(weekly);
      setError(categoriesFailed);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    getMyLives()
      .then((l) => {
        setLivesInfo(l);
        livesFetchedAt.current = Date.now();
        setNowTs(Date.now());
      })
      .catch((e) => console.error("Failed to load lives:", e));
  }, []);

  // Tick the regen countdown once a second while the pool isn't full.
  useEffect(() => {
    if (!livesInfo || livesInfo.lives >= livesInfo.max) return;
    const id = setInterval(() => setNowTs(Date.now()), 1000);
    return () => clearInterval(id);
  }, [livesInfo]);

  // Countdown timer to next weekly quiz
  useEffect(() => {
    if (!data?.week_end) return;
    const updateCountdown = () => {
      const now = new Date().getTime();
      const end = new Date(data.week_end!).getTime();
      const diff = end - now;
      if (diff <= 0) {
        setCountdown(t("quizzes.newQuizAvailable", "Quiz nou disponibil!"));
        return;
      }
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      setCountdown(`${days}z ${hours}h ${mins}m`);
    };
    updateCountdown();
    const interval = setInterval(updateCountdown, 60000);
    return () => clearInterval(interval);
  }, [data?.week_end, t]);

  const quiz = data?.current_quiz;
  const history = data?.quiz_history || [];

  const stripHtml = (html: string) => {
    const doc = new DOMParser().parseFromString(html, "text/html");
    return doc.body.textContent || "";
  };

  const openCategory = (name: string) => navigate(`/app/quizzes/category/${encodeURIComponent(name)}`);

  return (
    <MobileAppLayout>
      <div className="px-4 py-6 space-y-5">
        {/* Header — unified gradient hero */}
        <GameHero flow className="-mx-4 -mt-6 rounded-br-3xl px-4 pt-3 pb-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h1 className="text-xl font-extrabold">{t("quizzes.title", "Quiz-uri")}</h1>
              <p className="text-white/70 text-[13px] mt-0.5">{t("quizzes.categoriesSubtitle", "Alege o categorie și învață")}</p>
            </div>
            {livesInfo && (
              <div className="flex flex-col items-end gap-0.5 shrink-0">
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/15 backdrop-blur-sm ring-1 ring-white/15">
                  <LivesBadge lives={livesInfo.lives} max={livesInfo.max} />
                </div>
                {livesInfo.lives < livesInfo.max && (
                  <span className="text-white/70 text-[10px] font-semibold tabular-nums">
                    {(() => {
                      const ms = Math.max(0, livesInfo.ms_to_next_life - (nowTs - livesFetchedAt.current));
                      const s = Math.ceil(ms / 1000);
                      return `+1 ${t("quizzes.livesIn", "în")} ${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
                    })()}
                  </span>
                )}
              </div>
            )}
          </div>
        </GameHero>

        {/* ── Categories (primary) ────────────────────────────────── */}
        {loading ? (
          <div className="grid grid-cols-2 gap-3 animate-fadeIn">
            {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-40 rounded-2xl" />)}
          </div>
        ) : error ? (
          <GameCard className="p-6 text-center">
            <h3 className="text-base font-bold text-slate-800 mb-1">{t("common.loadErrorTitle", "Ceva n-a mers")}</h3>
            <p className="text-sm text-slate-500">
              {t("common.loadError", "Nu am putut încărca datele. Verifică conexiunea și încearcă din nou.")}
            </p>
            <button
              onClick={loadData}
              className="mt-3 px-4 py-2 text-sm font-semibold rounded-lg bg-indigo-600 text-white"
            >
              {t("common.retry", "Reîncearcă")}
            </button>
          </GameCard>
        ) : categories.length === 0 ? (
          <GameCard className="p-8 text-center">
            <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-indigo-100 to-blue-100 rounded-full flex items-center justify-center">
              <GameIcon name="bulb" className="w-10 h-10" />
            </div>
            <h3 className="text-xl font-bold text-slate-800 mb-2">{t("quizzes.noCategories", "Nicio categorie disponibilă")}</h3>
            <p className="text-slate-500">{t("quizzes.noCategoriesDesc", "Vor apărea aici de îndată ce sunt adăugate quiz-uri")}</p>
          </GameCard>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {categories.map((c) => {
              const empty = c.total === 0;
              const done = c.completed >= c.total && c.total > 0;
              const g = CATEGORY_GRADIENTS[hashString(c.name) % CATEGORY_GRADIENTS.length];
              return (
                <button
                  key={c.id}
                  onClick={empty ? undefined : () => openCategory(c.name)}
                  disabled={empty}
                  aria-disabled={empty || undefined}
                  className={`group relative aspect-square text-left rounded-2xl overflow-hidden shadow-[0_6px_18px_rgba(2,6,23,0.14)] ring-1 ring-black/5 transition-transform duration-150 ${
                    empty ? "cursor-default" : "active:scale-[0.97]"
                  }`}
                >
                  {/* Full-bleed background: category image if set, else a
                      colourful gradient with a big glyph + initial. */}
                  {c.image_url ? (
                    <img
                      src={resolveMediaUrl(c.image_url) || undefined}
                      alt={c.title}
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  ) : (
                    <div className={`absolute inset-0 bg-gradient-to-br ${g} grid place-items-center`}>
                      <GameIcon name="bulb" className="w-16 h-16 text-white/90 drop-shadow" />
                      <span className="absolute -bottom-5 -right-2 text-white/15 font-black text-8xl leading-none select-none">
                        {c.title.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}

                  {/* Scrim so the overlaid text stays legible over any image */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/25 to-transparent" />

                  {/* New badge */}
                  {c.new_count > 0 && (
                    <span className="absolute top-2 right-2 z-10 px-2 py-0.5 rounded-full bg-amber-400 text-amber-900 text-[10px] font-extrabold shadow-[0_2px_0_#b45309]">
                      +{c.new_count} {t("quizzes.new", "noi")}
                    </span>
                  )}
                  {/* Quiz count — prominent, always visible over the image */}
                  {!empty && (
                    <span className="absolute top-2 left-2 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-black/45 backdrop-blur-sm text-white text-[11px] font-extrabold tabular-nums ring-1 ring-white/15">
                      <GameIcon name="bulb" className="w-3.5 h-3.5" />
                      {c.total} {c.total === 1 ? t("quizzes.quizOne", "quiz") : t("quizzes.quizMany", "quiz-uri")}
                    </span>
                  )}

                  {/* Bottom overlay: title + progress */}
                  <div className="absolute inset-x-0 bottom-0 p-3.5">
                    <h3 className="text-white font-extrabold text-[15px] leading-snug line-clamp-2 drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]">{c.title}</h3>

                    {empty ? (
                      <div className="mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/15 backdrop-blur-sm text-white/90">
                        <GameIcon name="bolt" className="w-3 h-3" />
                        <span className="text-[10px] font-bold">{t("quizzes.comingSoon", "În curând")}</span>
                      </div>
                    ) : (
                      <>
                        <div className="mt-1.5 flex items-center justify-between text-[11px] font-bold text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]">
                          <span className="tabular-nums">{c.completed}/{c.total} {t("quizzes.completedShort", "finalizate")}</span>
                          <span className="tabular-nums">{c.percent}%</span>
                        </div>
                        <div className="mt-1.5 h-2 rounded-full bg-white/30 overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${done ? "bg-emerald-400" : "bg-white"}`}
                            style={{ width: `${Math.max(c.percent, c.percent > 0 ? 6 : 0)}%` }}
                          />
                        </div>
                      </>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {/* ── Weekly quiz (secondary) ─────────────────────────────── */}
        {!loading && quiz && (
          <div className="space-y-3">
            <h2 className="text-lg font-bold text-slate-800">{t("quizzes.weeklyQuiz", "Quiz-ul Săptămânii")}</h2>
            <GameCard
              className="overflow-hidden cursor-pointer active:translate-y-[2px] transition-transform"
              onClick={() => navigate(`/quiz/${quiz.id}`)}
            >
              <GameHero className="h-28">
                <div className="absolute inset-0 flex items-center justify-center">
                  <GameIcon name="bulb" className="w-14 h-14 opacity-20" />
                </div>
                <div className="absolute top-3 right-3">
                  {quiz.is_completed ? (
                    <span className="px-2.5 py-1 rounded-full text-xs font-extrabold shadow-[0_2px_0_#059669] bg-emerald-500 text-white flex items-center gap-1">
                      <GameIcon name="check" className="w-3 h-3 text-white" />
                      {t("quiz.completed", "Completat")}
                    </span>
                  ) : (
                    <span className="px-2.5 py-1 rounded-full text-xs font-extrabold shadow-[0_2px_0_#b45309] bg-amber-400 text-amber-900">
                      {t("quiz.new", "Nou")}
                    </span>
                  )}
                </div>
                <div className="absolute bottom-3 right-3">
                  <span className="px-2.5 py-1 bg-white rounded-full text-xs font-extrabold text-amber-600 shadow-[0_2px_0_#fcd34d] flex items-center gap-1">
                    <GameIcon name="star" className="w-3.5 h-3.5" />
                    {t("quiz.up_to", "până la")} +{quiz.max_points ?? quiz.reward_points}
                  </span>
                </div>
              </GameHero>
              <div className="p-5">
                <h3 className="font-bold text-slate-800 text-xl leading-tight">{quiz.title}</h3>
                {quiz.description && <p className="text-slate-500 text-sm mt-2 line-clamp-2">{stripHtml(quiz.description)}</p>}
                <div className="flex items-center gap-4 mt-3">
                  <div className="flex items-center gap-1.5 text-slate-500">
                    <GameIcon name="bulb" className="w-4 h-4" />
                    <span className="text-sm">{quiz.question_count} {t("quiz.questions", "întrebări")}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-slate-500">
                    <GameIcon name="bolt" className="w-4 h-4" />
                    <span className="text-sm capitalize">{quiz.difficulty}</span>
                  </div>
                </div>
                <GameButton variant={quiz.is_completed ? "ghost" : "primary"} className="mt-4">
                  {quiz.is_completed ? t("quiz.tryAgain", "Încearcă din nou") : t("quiz.startQuiz", "Începe Quiz")}
                </GameButton>
              </div>
            </GameCard>

            {countdown && (
              <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-2xl p-4 text-center shadow-[0_3px_0_#0f172a]">
                <p className="text-slate-400 text-xs uppercase tracking-wider font-bold mb-1">{t("quizzes.nextQuizIn", "Următorul quiz în")}</p>
                <p className="text-white text-xl font-bold font-mono">{countdown}</p>
              </div>
            )}
          </div>
        )}

        {/* ── History ─────────────────────────────────────────────── */}
        {!loading && history.length > 0 && (
          <div>
            <h2 className="text-lg font-bold text-slate-800 mb-3">{t("quizzes.history", "Istoric quiz-uri")}</h2>
            <div className="space-y-2.5">
              {history.map((item, idx) => (
                <GameCard
                  key={`${item.quiz_id}-${idx}`}
                  onClick={() => navigate(`/quiz/${item.quiz_id}`)}
                  className="p-4 flex items-center gap-3 cursor-pointer active:translate-y-[2px] transition-transform"
                >
                  <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <GameIcon name="check" className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-slate-800 truncate">{item.title}</p>
                    <p className="text-xs text-slate-400">{formatDate(new Date(item.completed_at))}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-extrabold text-emerald-600 tabular-nums">{item.score}%</p>
                  </div>
                </GameCard>
              ))}
            </div>
          </div>
        )}
      </div>
    </MobileAppLayout>
  );
}
